import React from 'react';
import { countryOptions } from '../../utils/formConstants';
import SingleCalendarDateComparisonSidebar from './SingleCalendarDateComparisonSidebar';
import { CalendarDaysIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

/**
 * Compact analysis form for sidebar use.
 * Optimized for narrow 360px width with essential controls only.
 */
export default function SidebarAnalysisForm({
  onSubmit,
  onCancel,
  analysisFlow,
  darkMode,
  disabled = false,
}) {
  // Internal form state
  const [formState, setFormState] = React.useState({
    dates: {
      oldStartDate: null,
      oldEndDate: null,
      newStartDate: null,
      newEndDate: null
    },
    country: '',
    useCountryScaling: false,
    collectHistorical: true // Default to true - most users want this
  });

  // Provide default analysisFlow
  const defaultAnalysisFlow = { isRunning: false, message: '', error: '', progress: 0 };
  const currentAnalysisFlow = analysisFlow || defaultAnalysisFlow;

  // Destructure form state
  const { dates, country, useCountryScaling, collectHistorical } = formState;

  // Validation
  const areDatesValid = () => {
    return dates.oldStartDate && dates.oldEndDate && dates.newStartDate && dates.newEndDate;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!areDatesValid()) return;
    
    onSubmit(formState);
  };

  // Handle input changes
  const updateFormState = (updates) => {
    setFormState(prev => ({ ...prev, ...updates }));
  };

  // Handle date changes from SingleCalendarDateComparison
  const handleDatesChange = (newDates) => {
    updateFormState({ dates: newDates });
  };

  const handleCountryChange = (e) => {
    updateFormState({ country: e.target.value });
  };

  const handleCountryScalingChange = (e) => {
    updateFormState({ useCountryScaling: e.target.checked });
  };

  // Handle historical data collection change
  const handleHistoricalCollectionChange = (e) => {
    updateFormState({ collectHistorical: e.target.checked });
  };

  // Form state
  const isFormDisabled = disabled || currentAnalysisFlow.isRunning;

  const getButtonText = () => {
    if (currentAnalysisFlow.isRunning) {
      return currentAnalysisFlow.message || 'Analyzing...';
    }
    return 'Run Analysis';
  };

  return (
    <div className="space-y-6 pb-6 md:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
          New Analysis
        </h3>
        <button
          onClick={onCancel}
          className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date Range Selection Section */}
        <div>
          <div className="flex items-center mb-3">
            <CalendarDaysIcon className={`w-5 h-5 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <h4 className={`text-base font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Date Ranges
            </h4>
          </div>
          <SingleCalendarDateComparisonSidebar
            oldStartDate={dates.oldStartDate}
            oldEndDate={dates.oldEndDate}
            newStartDate={dates.newStartDate}
            newEndDate={dates.newEndDate}
            onDatesChange={handleDatesChange}
            darkMode={darkMode}
            disabled={isFormDisabled}
          />
        </div>

        {/* Country Filter Section */}
        <div>
          <div className="flex items-center mb-3">
            <GlobeAltIcon className={`w-5 h-5 mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            <h4 className={`text-base font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Country Filter
            </h4>
          </div>
          <select
            value={country}
            onChange={handleCountryChange}
            disabled={isFormDisabled}
            className={`w-full rounded-md border shadow-sm text-sm py-2 px-3
              ${darkMode
                ? 'bg-gray-700 border-gray-600 text-gray-200'
                : 'bg-white border-gray-300 text-gray-900'
              }
              ${isFormDisabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {countryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Country scaling option */}
          {country && country !== "" && (
            <div className="mt-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useCountryScaling}
                  onChange={handleCountryScalingChange}
                  disabled={isFormDisabled}
                  className={`rounded ${darkMode ? 'bg-gray-700 border-gray-600' : 'border-gray-300'} ${isFormDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                <span className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${isFormDisabled ? 'opacity-50' : ''}`}>
                  Use country scaling
                </span>
              </label>
            </div>
          )}

          {/* Historical Data Collection Option */}
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={collectHistorical}
                onChange={handleHistoricalCollectionChange}
                disabled={isFormDisabled}
                className={`mt-0.5 rounded ${darkMode ? 'bg-gray-700 border-gray-600' : 'border-gray-300'} ${isFormDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              <div className="ml-2 flex-1">
                <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${isFormDisabled ? 'opacity-50' : ''}`}>
                  Collect historical data
                </span>
                <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${isFormDisabled ? 'opacity-50' : ''}`}>
                  {collectHistorical
                    ? "Enables trending analysis and year-over-year insights" 
                    : "Skip historical data collection (can collect later)"}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Error Display */}
        {currentAnalysisFlow.error && (
          <div className="text-center text-red-500 bg-red-100 dark:bg-red-900/30 py-2 px-3 rounded-md text-sm">
            {currentAnalysisFlow.error}
          </div>
        )}

        {/* Validation Error */}
        {!areDatesValid() && (
          <div className="text-center text-red-500 bg-red-100 dark:bg-red-900/30 py-2 px-3 rounded-md text-sm">
            Please select date ranges
          </div>
        )}

        {/* Progress Display */}
        {currentAnalysisFlow.isRunning && currentAnalysisFlow.progress > 0 && (
          <div>
            <div className={`w-full bg-gray-200 rounded-full h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${currentAnalysisFlow.progress}%` }}
              ></div>
            </div>
            <p className={`text-xs text-center mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {currentAnalysisFlow.message} ({currentAnalysisFlow.progress}%)
            </p>
          </div>
        )}

        {/* Submit Button */}
        <div className="pb-4 md:pb-0">
          <button
            type="submit"
            disabled={isFormDisabled || !areDatesValid()}
            className={`
              w-full py-3 px-4 rounded-lg font-medium text-white transition-all duration-200
              shadow-sm hover:shadow-md active:shadow-inner active:scale-[0.98]
              transform active:translate-y-0.5 text-sm flex items-center justify-center
              ${(isFormDisabled || !areDatesValid())
                ? 'bg-gray-500 cursor-not-allowed opacity-70'
                : darkMode
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800'
              }
              ${currentAnalysisFlow.isRunning ? 'animate-pulse' : ''}
            `}
          >
            {currentAnalysisFlow.isRunning && (
              <svg 
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                ></circle>
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {getButtonText()}
          </button>
        </div>
      </form>
    </div>
  );
} 